import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useNewestListings = () => {
  return useQuery({
    queryKey: ['newest-listings'],
    queryFn: async () => {
      // Query for active listings that should be publicly visible
      const { data, error } = await supabase
        .from('listings')
        .select(`
          id,
          title,
          price,
          breed,
          type,
          location,
          images,
          price_currency,
          calving_ease,
          birth_weight,
          weaning_weight,
          milk,
          breeder_id,
          status,
          listing_type
        `)
        .eq('status', 'active') // Only fetch active listings
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching active listings:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Get unique breeder IDs
      const breederIds = [...new Set(data.map(listing => listing.breeder_id))];
      
      // Fetch profiles for these breeders
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, business_name, first_name, last_name')
        .in('id', breederIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Create a map for quick profile lookup
      const profileMap = new Map(profiles?.map(profile => [profile.id, profile]) || []);

      // Transform the data to match the expected format
      const transformedData = data.map(listing => {
        const profile = profileMap.get(listing.breeder_id);
        
        // Handle images - convert from Json to string
        let imageUrl = "https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=400&h=300&fit=crop";
        if (listing.images && Array.isArray(listing.images) && listing.images.length > 0) {
          imageUrl = String(listing.images[0]);
        }
        
        return {
          id: listing.id,
          title: listing.title,
          image: imageUrl,
          price: listing.price ? `${listing.price_currency || '$'} ${listing.price.toLocaleString()}` : undefined,
          location: listing.location || 'Location not specified',
          breeder: profile?.business_name || 
                  `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 
                  'Unknown Breeder',
          breed: listing.breed,
          type: listing.type,
          listing_type: listing.listing_type,
          evaluationData: {
            calvingEase: listing.calving_ease,
            birthWeight: listing.birth_weight,
            weaningWeight: listing.weaning_weight,
            milk: listing.milk
          }
        };
      });

      return transformedData;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (renamed from cacheTime)
  });
};
